import React, { useCallback, useReducer, useEffect } from 'react';

import { useLayoutEffect } from '~app/hooks';

import Context from './Context';
import reducer from './reducer';
import { intersects, scrollIntoView } from './utils';
import { ScrollAnchorRef } from './types';

export type Props = React.PropsWithChildren<{
  /** Used to turn the progression-engine on (OQAAT) and off (Classic) - Not yet implemented */
  active?: boolean;
}>;

function Provider({ children, active = true }: Props): React.ReactElement {
  const [{ registered: anchors, active: activeAnchor }, dispatch] = useReducer(reducer, {
    active: null,
    registered: [],
  });

  const findActiveAnchor = useCallback(() => {
    const winMidpoint = window.innerHeight / 2 + window.scrollY + 5;
    const currentAnchor = anchors.find(anchor => {
      const el = anchor?.current;
      if (!el) return false;
      return intersects(winMidpoint, el.offsetTop, el.offsetTop + el.offsetHeight);
    });

    if (currentAnchor && currentAnchor !== activeAnchor) {
      dispatch({ type: 'ACTIVATE', payload: currentAnchor });
    }
  }, [activeAnchor, anchors, dispatch]);

  useLayoutEffect(() => {
    window.addEventListener('scroll', findActiveAnchor);
    return () => window.removeEventListener('scroll', findActiveAnchor);
  }, [findActiveAnchor]);

  useEffect(() => {
    // Run on registration (a.k.a when anchors change)
    findActiveAnchor();
  }, [anchors, findActiveAnchor]);

  const scrollTo = useCallback(
    (ref: ScrollAnchorRef) => {
      if (ref === activeAnchor || !ref.current) return;
      scrollIntoView(ref.current);
    },
    [activeAnchor]
  );

  const nextQuestion = useCallback(
    (e: React.MouseEvent) => {
      if (!active) return;
      const activeRefIdx = !activeAnchor ? 0 : anchors.findIndex(a => a === activeAnchor);

      if (activeRefIdx > -1) {
        const ref = anchors[activeRefIdx + 1];
        scrollTo(ref);
      }
    },
    [anchors, activeAnchor, active, scrollTo]
  );

  const register = useCallback(payload => dispatch({ type: 'REGISTER', payload }), [dispatch]);
  const unregister = useCallback(payload => dispatch({ type: 'UNREGISTER', payload }), [dispatch]);

  return (
    <Context.Provider
      value={{
        anchors,
        nextQuestion,
        activeAnchor,
        scrollTo,
        register,
        unregister,
      }}
    >
      <>{children}</>
    </Context.Provider>
  );
}

export default Provider;
